/**
 * @package EGI — FreeAiChat
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 1.0.0 (FlorenceEGI — EGI)
 * @date 2026-04-06
 * @purpose Vanilla JS IIFE for Free AI Chat — SSE streaming, conversations, mobile drawer
 */
(function () {
    'use strict';

    var state = {
        messages: [], conversationId: null, isStreaming: false,
        remaining: 50, limit: 50, isAuth: false, userId: null,
        sessionId: null,
        endpoints: { chat: '', remaining: '', conversations: '', showcase: '' },
        showcase: { items: [], currentIndex: 0, timer: null },
    };
    var el = {};

    function init() {
        var app = document.getElementById('fai-app');
        if (!app) return;

        state.isAuth = app.dataset.isAuth === '1';
        state.userId = app.dataset.userId || null;
        state.sessionId = app.dataset.sessionId || '';
        state.remaining = parseInt(app.dataset.remaining, 10) || 50;
        state.limit = parseInt(app.dataset.limit, 10) || 50;
        state.endpoints.chat = app.dataset.chatEndpoint || '/free-ai/chat';
        state.endpoints.remaining = app.dataset.remainingEndpoint || '/free-ai/remaining';
        state.endpoints.conversations = app.dataset.conversationsEndpoint || '/free-ai/conversations';
        state.endpoints.showcase = app.dataset.showcaseEndpoint || '/free-ai/hyper-egis';

        el.messages = document.getElementById('fai-messages');
        el.welcome = document.getElementById('fai-welcome');
        el.input = document.getElementById('fai-input');
        el.sendBtn = document.getElementById('fai-send');
        el.inputWrapper = document.getElementById('fai-input-wrapper');
        el.counterBadge = document.getElementById('fai-counter-badge');
        el.counterText = document.getElementById('fai-counter-text');
        el.rateLimitMsg = document.getElementById('fai-rate-limit');
        el.overlay = document.getElementById('fai-overlay');
        el.mobileSidebar = document.getElementById('fai-mobile-sidebar');
        el.sidebarToggle = document.getElementById('fai-sidebar-toggle');
        el.showcase = document.getElementById('fai-showcase');
        el.showcaseImage = document.getElementById('fai-showcase-image');
        el.showcaseInfo = document.getElementById('fai-showcase-info');

        bindEvents();
        updateCounter();
        loadShowcase();
        if (state.isAuth) loadConversations();
    }

    function bindEvents() {
        el.sendBtn.addEventListener('click', sendMessage);
        el.input.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
        });
        el.input.addEventListener('input', function () {
            this.style.height = 'auto';
            this.style.height = Math.min(this.scrollHeight, 120) + 'px';
            el.sendBtn.disabled = !this.value.trim();
        });
        if (el.sidebarToggle) el.sidebarToggle.addEventListener('click', toggleSidebar);
        if (el.overlay) el.overlay.addEventListener('click', closeSidebar);
        document.querySelectorAll('.js-fai-new-chat').forEach(function (b) { b.addEventListener('click', newChat); });
        setupSwipeToClose();
    }

    // --- Send + SSE Stream ---
    function sendMessage() {
        var text = el.input.value.trim();
        if (!text || state.isStreaming || state.remaining <= 0) return;
        if (el.welcome) el.welcome.style.display = 'none';

        appendMessage('user', text);
        el.input.value = '';
        el.input.style.height = 'auto';
        el.sendBtn.disabled = true;
        state.isStreaming = true;

        var typingEl = showTypingIndicator();
        var csrf = document.querySelector('meta[name="csrf-token"]');

        fetch(state.endpoints.chat, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrf ? csrf.content : '',
                'Accept': 'text/event-stream',
            },
            body: JSON.stringify({
                message: text,
                conversation_id: state.conversationId,
                session_id: state.sessionId,
                conversation_history: state.messages.slice(-20),
                web_search: !!(document.getElementById('fai-web-search-toggle') && document.getElementById('fai-web-search-toggle').checked),
            }),
        }).then(function (res) {
            removeTypingIndicator(typingEl);
            if (!res.ok) {
                if (res.status === 429) { handleRateLimit(); return; }
                throw new Error('HTTP ' + res.status);
            }
            return handleStream(res);
        }).then(function () {
            state.isStreaming = false;
            state.remaining = Math.max(0, state.remaining - 1);
            updateCounter();
        }).catch(function (err) {
            removeTypingIndicator(typingEl);
            state.isStreaming = false;
            appendMessage('error', 'Si e\u0027 verificato un errore. Riprova.');
            console.error('FreeAI error:', err);
        });
    }

    function handleStream(response) {
        var reader = response.body.getReader();
        var decoder = new TextDecoder();
        var fullMsg = '';
        var msgEl = appendMessage('ai', '');
        var bubble = msgEl.querySelector('.fai-msg__bubble');
        var curEvent = '';

        function pump() {
            return reader.read().then(function (r) {
                if (r.done) { state.messages.push({ role: 'assistant', content: fullMsg }); return; }
                var lines = decoder.decode(r.value).split('\n');
                for (var i = 0; i < lines.length; i++) {
                    var ln = lines[i];
                    if (ln.startsWith('event: ')) { curEvent = ln.slice(7).trim(); }
                    else if (ln.startsWith('data: ')) {
                        var raw = ln.slice(6);
                        if (curEvent === 'conversation_id') {
                            try { var c = JSON.parse(raw); if (c.conversation_id) state.conversationId = c.conversation_id; } catch (_) {}
                        } else if (curEvent === 'memory_saved') {
                            appendMessage('memory', 'Memoria salvata.');
                        } else if (curEvent !== 'done') {
                            try { var d = JSON.parse(raw); if (d.content) fullMsg += d.content; } catch (_) { fullMsg += raw; }
                            if (bubble) bubble.innerHTML = renderMarkdown(fullMsg);
                            scrollToBottom();
                        }
                        curEvent = '';
                    }
                }
                return pump();
            });
        }
        return pump();
    }

    // --- Render Messages ---
    function appendMessage(role, content) {
        var div = document.createElement('div');
        div.className = 'fai-msg fai-msg--' + role;

        if (role === 'error' || role === 'memory') {
            div.innerHTML = '<div class="fai-msg__bubble">' + escapeHtml(content) + '</div>';
            el.messages.appendChild(div); scrollToBottom(); return div;
        }

        var avatar = role === 'ai'
            ? '<div class="fai-msg__avatar" aria-hidden="true">F</div>'
            : '<div class="fai-msg__avatar" aria-hidden="true">' + (state.isAuth ? 'U' : 'G') + '</div>';

        var copyHtml = role === 'ai'
            ? '<button type="button" class="fai-msg__copy js-fai-copy" aria-label="Copy"><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg></button>'
            : '';

        var rendered = role === 'ai' ? renderMarkdown(content) : escapeHtml(content);
        div.innerHTML = avatar + '<div class="fai-msg__bubble">' + copyHtml + rendered + '</div>';

        var copyBtn = div.querySelector('.js-fai-copy');
        if (copyBtn) {
            copyBtn.addEventListener('click', function () {
                var t = this.closest('.fai-msg__bubble').textContent || '';
                navigator.clipboard.writeText(t.trim()).catch(function () {});
            });
        }

        if (role === 'user') state.messages.push({ role: 'user', content: content });
        el.messages.appendChild(div); scrollToBottom(); return div;
    }

    function showTypingIndicator() {
        var d = document.createElement('div');
        d.className = 'fai-msg fai-msg--ai'; d.id = 'fai-typing';
        d.innerHTML = '<div class="fai-msg__avatar" aria-hidden="true">F</div>' +
            '<div class="fai-msg__bubble fai-typing" aria-label="Typing">' +
            '<span class="fai-typing__dot"></span><span class="fai-typing__dot"></span><span class="fai-typing__dot"></span></div>';
        el.messages.appendChild(d); scrollToBottom(); return d;
    }

    function removeTypingIndicator(t) { if (t && t.parentNode) t.parentNode.removeChild(t); }

    // --- Counter ---
    function updateCounter() {
        if (!el.counterBadge) return;
        el.counterBadge.textContent = state.remaining;
        el.counterBadge.className = 'fai-counter__badge';
        var ratio = state.remaining / state.limit;
        el.counterBadge.classList.add(ratio > 0.5 ? 'fai-counter__badge--green' : ratio > 0.2 ? 'fai-counter__badge--yellow' : 'fai-counter__badge--red');
        el.counterText.textContent = '/' + state.limit + ' messaggi rimanenti';
        if (state.remaining <= 0) handleRateLimit();
    }

    function handleRateLimit() {
        state.remaining = 0;
        if (el.inputWrapper) el.inputWrapper.classList.add('fai-input-wrapper--disabled');
        if (el.rateLimitMsg) {
            el.rateLimitMsg.style.display = 'block';
            el.rateLimitMsg.textContent = state.isAuth
                ? 'Hai raggiunto il limite giornaliero. Riprova domani.'
                : 'Limite raggiunto. Registrati per continuare!';
        }
        updateCounter();
    }

    // --- Conversations (auth) ---
    function loadConversations() {
        if (!state.isAuth) return;
        var csrf = document.querySelector('meta[name="csrf-token"]');
        fetch(state.endpoints.conversations, {
            headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf ? csrf.content : '' },
        }).then(function (r) { return r.json(); }).then(function (data) {
            document.querySelectorAll('.js-fai-conversations').forEach(function (c) {
                c.innerHTML = '';
                (data.conversations || []).forEach(function (conv) {
                    var item = document.createElement('div');
                    item.className = 'fai-sidebar__conv-item';
                    item.setAttribute('role', 'listitem');
                    item.setAttribute('tabindex', '0');
                    item.textContent = conv.title || 'Conversazione';
                    item.dataset.convId = conv.id;
                    item.addEventListener('click', function () { loadConversation(conv.id); });
                    item.addEventListener('keydown', function (e) { if (e.key === 'Enter') loadConversation(conv.id); });
                    c.appendChild(item);
                });
            });
        }).catch(function (e) { console.warn('FreeAI: conversations load error', e); });
    }

    function loadConversation(convId) {
        if (!state.isAuth) return;
        var csrf = document.querySelector('meta[name="csrf-token"]');
        fetch(state.endpoints.conversations + '/' + convId, {
            headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrf ? csrf.content : '' },
        }).then(function (r) { return r.json(); }).then(function (data) {
            state.conversationId = convId;
            state.messages = [];
            el.messages.innerHTML = '';
            if (el.welcome) el.welcome.style.display = 'none';
            (data.messages || []).forEach(function (m) {
                appendMessage(m.role === 'assistant' ? 'ai' : 'user', m.content);
            });
            document.querySelectorAll('.fai-sidebar__conv-item').forEach(function (i) {
                i.classList.toggle('fai-sidebar__conv-item--active', i.dataset.convId === String(convId));
            });
            closeSidebar();
        }).catch(function (e) { console.error('FreeAI: conversation load error', e); });
    }

    // --- New Chat ---
    function newChat() {
        state.conversationId = null; state.messages = [];
        el.messages.innerHTML = '';
        var w = document.createElement('div');
        w.id = 'fai-welcome'; w.className = 'fai-welcome';
        w.innerHTML = '<div class="fai-welcome__header">' +
            '<div class="fai-welcome__brand">' +
            '<div class="fai-welcome__logo"><svg class="h-full w-full" style="color:var(--fai-accent)" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></div>' +
            '<span class="fai-welcome__brand-name">FlorenceEGI</span>' +
            '</div>' +
            '<span class="fai-welcome__brand-tag">Free AI</span>' +
            '</div>' +
            '<p class="fai-welcome__subtitle">Il tuo assistente AI gratuito.</p>';
        el.messages.appendChild(w); el.welcome = w;
        // Re-add showcase after welcome
        var sc = document.createElement('div');
        sc.id = 'fai-showcase'; sc.className = 'fai-showcase';
        sc.setAttribute('role', 'region');
        sc.setAttribute('aria-label', 'Opere d\'arte in evidenza');
        sc.innerHTML = '<div class="fai-showcase__stage">' +
            '<div id="fai-showcase-image" class="fai-showcase__image-wrap"></div>' +
            '<div id="fai-showcase-info" class="fai-showcase__info"></div></div>';
        el.messages.appendChild(sc);
        el.showcase = sc;
        el.showcaseImage = document.getElementById('fai-showcase-image');
        el.showcaseInfo = document.getElementById('fai-showcase-info');
        if (state.showcase.items.length) renderShowcaseSlide(state.showcase.currentIndex);
        document.querySelectorAll('.fai-sidebar__conv-item').forEach(function (i) { i.classList.remove('fai-sidebar__conv-item--active'); });
        closeSidebar();
    }

    // --- Showcase Carousel ---
    function loadShowcase() {
        if (!el.showcase) return;
        fetch(state.endpoints.showcase, {
            headers: { 'Accept': 'application/json' },
        }).then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
        }).then(function (data) {
            var items = data.egis || data || [];
            if (!items.length) { el.showcase.style.display = 'none'; return; }
            state.showcase.items = items;
            state.showcase.currentIndex = 0;
            renderShowcaseSlide(0);
            if (items.length > 1) {
                state.showcase.timer = setInterval(function () {
                    var next = (state.showcase.currentIndex + 1) % state.showcase.items.length;
                    transitionShowcase(next);
                }, 5000);
            }
        }).catch(function (e) {
            console.warn('FreeAI: showcase load error', e);
            if (el.showcase) el.showcase.style.display = 'none';
        });
    }

    function renderShowcaseSlide(index) {
        var item = state.showcase.items[index];
        if (!item || !el.showcaseImage) return;
        state.showcase.currentIndex = index;

        var img = document.createElement('img');
        img.src = item.image_url;
        img.alt = escapeHtml(item.title) + ' — ' + escapeHtml(item.creator_name);
        img.className = 'fai-showcase__image';
        img.loading = 'eager';

        var link = document.createElement('a');
        link.href = item.share_url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.className = 'fai-showcase__link';
        link.setAttribute('aria-label', 'Apri opera: ' + item.title);
        link.appendChild(img);

        el.showcaseImage.innerHTML = '';
        el.showcaseImage.appendChild(link);

        el.showcaseInfo.innerHTML =
            '<span class="fai-showcase__title">' + escapeHtml(item.title) + '</span>' +
            '<span class="fai-showcase__creator">di ' + escapeHtml(item.creator_name) + '</span>';
    }

    function transitionShowcase(nextIndex) {
        if (!el.showcaseImage) return;
        el.showcaseImage.classList.add('fai-showcase__fade-out');
        el.showcaseInfo.classList.add('fai-showcase__fade-out');
        setTimeout(function () {
            renderShowcaseSlide(nextIndex);
            el.showcaseImage.classList.remove('fai-showcase__fade-out');
            el.showcaseInfo.classList.remove('fai-showcase__fade-out');
        }, 500);
    }

    // --- Sidebar (Mobile) ---
    function toggleSidebar() {
        el.mobileSidebar.classList.contains('fai-sidebar--open') ? closeSidebar() : openSidebar();
    }
    function openSidebar() {
        el.mobileSidebar.classList.add('fai-sidebar--open');
        el.overlay.classList.add('fai-overlay--visible');
        el.overlay.setAttribute('aria-hidden', 'false');
    }
    function closeSidebar() {
        if (el.mobileSidebar) el.mobileSidebar.classList.remove('fai-sidebar--open');
        if (el.overlay) { el.overlay.classList.remove('fai-overlay--visible'); el.overlay.setAttribute('aria-hidden', 'true'); }
    }

    function setupSwipeToClose() {
        if (!el.mobileSidebar) return;
        var sx = 0, cx = 0, sw = false;
        el.mobileSidebar.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; sw = true; }, { passive: true });
        el.mobileSidebar.addEventListener('touchmove', function (e) {
            if (!sw) return; cx = e.touches[0].clientX;
            var d = sx - cx; if (d > 0) el.mobileSidebar.style.transform = 'translateX(' + (-d) + 'px)';
        }, { passive: true });
        el.mobileSidebar.addEventListener('touchend', function () {
            sw = false; if (sx - cx > 80) closeSidebar();
            el.mobileSidebar.style.transform = '';
        }, { passive: true });
    }

    // --- Markdown (basic, no external libs) ---
    function renderMarkdown(text) {
        if (!text) return '';
        var h = escapeHtml(text);
        h = h.replace(/```(\w*)\n?([\s\S]*?)```/g, function (_, _l, c) { return '<pre><code>' + c.trim() + '</code></pre>'; });
        h = h.replace(/`([^`]+)`/g, '<code>$1</code>');
        h = h.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        h = h.replace(/\*([^*]+)\*/g, '<em>$1</em>');
        h = h.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
        h = h.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>');
        h = h.replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>');
        h = h.replace(/<\/ul>\s*<ul>/g, '');
        h = h.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
        h = h.replace(/^#{1,3} (.+)$/gm, '<strong>$1</strong>');
        h = h.replace(/\n\n/g, '</p><p>');
        h = h.replace(/\n/g, '<br>');
        return '<p>' + h + '</p>';
    }

    // --- Utilities ---
    function escapeHtml(t) {
        var d = document.createElement('div'); d.appendChild(document.createTextNode(t)); return d.innerHTML;
    }
    function scrollToBottom() { if (el.messages) el.messages.scrollTop = el.messages.scrollHeight; }

    document.addEventListener('DOMContentLoaded', init);
})();
